/**
 * The report step's Käännökset panel (#1442): the report's stored translations,
 * each with its string count, an update (translate what changed) and a way to
 * remove it, and below them any languages to add. A report can be translated
 * into any language; one without a hand-written locale also gets its headings
 * and labels translated, once for every report. Runs the report.translate job
 * and follows it here.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Languages, RefreshCw, Trash2, Loader2 } from 'lucide-react';
import { languageName } from '@kuutio/shared/reportLanguages.js';
import API_ENDPOINTS from '../../config/api';
import useContentRefresh from '../../hooks/useContentRefresh';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { useConfirm } from '../DialogProvider.jsx';
import LanguagePicker from '../ui/LanguagePicker';
import { ActionRow, PrimaryButton, SecondaryButton, SectionLabel, StatusMessage } from './reportAdminStyles';

const POLL_MS = 2000;
const TERMINAL = new Set(['done', 'failed', 'cancelled']);

const rowStyle = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '6px 10px', marginBottom: 6, background: '#1f2937', borderRadius: 6, color: '#e5e7eb', fontSize: 13 };
const smallButton = { padding: '4px 10px', fontSize: 12, gap: 4 };

async function readError(res) {
  const err = await res.json().catch(() => ({}));
  return err.error?.message || err.error || `HTTP ${res.status}`;
}

export default function ReportTranslationsPanel({ instanceId, instanceLanguage }) {
  const { t, language } = useTranslation();
  const confirm = useConfirm();
  const [stored, setStored] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [includeTranscripts, setIncludeTranscripts] = useState(true);
  const [job, setJob] = useState(null); // { id, langs, status, stage, progress }
  const [removing, setRemoving] = useState(null);
  const [status, setStatus] = useState(null);
  const pollRef = useRef(null);

  const name = (lang) => languageName(lang, language);

  const refresh = useCallback(async () => {
    if (!instanceId) return;
    try {
      const res = await fetch(API_ENDPOINTS.REPORT_TRANSLATION_LANGUAGES(instanceId));
      if (res.ok) setStored((await res.json()).languages || []);
    } catch (err) {
      console.error('Failed to refresh translation languages:', err);
    }
  }, [instanceId]);
  useEffect(() => { refresh(); }, [refresh]);
  // The translate job's content event; a cheap read, so any report change refetches too
  useContentRefresh(instanceId, ['report'], refresh, { minIntervalMs: 5000 });
  useEffect(() => () => clearInterval(pollRef.current), []);

  const follow = (jobId, langs) => {
    setJob({ id: jobId, langs, status: 'queued' });
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(API_ENDPOINTS.AI_JOB(jobId), { credentials: 'include' });
        if (!res.ok) throw new Error(await readError(res));
        const j = await res.json();
        setJob({ id: jobId, langs, status: j.status, stage: j.stage, progress: j.progress });
        if (!TERMINAL.has(j.status)) return;
        clearInterval(pollRef.current);
        setJob(null);
        if (j.status === 'done') {
          const r = j.result || {};
          setStatus(r.failedBatches
            ? { error: true, text: t('reportGen.status.translatePartial', { failed: r.failedBatches }) }
            : { text: t('reportGen.status.translateDone', { translated: r.translated ?? 0, skipped: r.skippedUnchanged ?? 0 }) });
        } else {
          setStatus({ error: true, text: `${t('reportGen.status.translateFailed')}: ${j.error || j.status}` });
        }
        refresh();
      } catch (err) {
        clearInterval(pollRef.current);
        setJob(null);
        setStatus({ error: true, text: `${t('reportGen.status.translateFailed')}: ${err.message}` });
      }
    }, POLL_MS);
  };

  const translate = async (langs) => {
    setStatus(null);
    try {
      const res = await fetch(API_ENDPOINTS.REPORT_TRANSLATE(instanceId), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ languages: langs, includeTranscripts }),
      });
      if (!res.ok) throw new Error(await readError(res));
      const { jobId } = await res.json();
      if (langs === chosen) setChosen([]);
      follow(jobId, langs);
    } catch (err) {
      setStatus({ error: true, text: `${t('reportGen.status.translateFailed')}: ${err.message}` });
    }
  };

  const remove = async (lang) => {
    const label = name(lang);
    if (!(await confirm({ message: t('reportGen.translate.removeConfirm', { lang: label }), confirmLabel: t('common.delete'), destructive: true }))) return;
    setRemoving(lang);
    setStatus(null);
    try {
      const res = await fetch(API_ENDPOINTS.REPORT_TRANSLATION_DELETE(instanceId, lang), { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error(await readError(res));
      const data = await res.json();
      setStatus({ text: t('reportGen.translate.removeDone', { lang: label, deleted: data.deleted ?? 0 }) });
      await refresh();
    } catch (err) {
      setStatus({ error: true, text: `${t('reportGen.translate.removeFailed')}: ${err.message}` });
    } finally {
      setRemoving(null);
    }
  };

  const busy = Boolean(job);

  return (
    <div style={{ marginBottom: 24 }}>
      <SectionLabel>{t('reportGen.translate.panelTitle')}</SectionLabel>
      {stored.length === 0 && <div style={{ color: '#888', fontSize: 13, marginBottom: 12 }}>{t('reportStep.noTranslations')}</div>}
      {stored.map((row) => {
        const lang = row.language;
        return (
          <div key={lang} style={rowStyle}>
            <strong>{name(lang)}</strong>
            <span style={{ color: '#9ca3af' }}>{languageName(lang, lang) !== name(lang) ? `${languageName(lang, lang)} · ` : ''}{lang}</span>
            <span style={{ color: '#9ca3af' }}>{t('reportGen.translate.existingCount', { count: row.count })}</span>
            {row.latest && <span style={{ color: '#9ca3af' }}>{t('reportGen.translate.latest', { date: new Date(row.latest).toLocaleDateString(language) })}</span>}
            <span style={{ flex: 1 }} />
            <SecondaryButton type="button" onClick={() => translate([lang])} disabled={busy} title={t('reportGen.translate.updateTitle')} style={smallButton}>
              <RefreshCw size={12} /> {t('reportGen.translate.update')}
            </SecondaryButton>
            <SecondaryButton type="button" onClick={() => remove(lang)} disabled={busy || removing === lang} style={smallButton}>
              <Trash2 size={12} /> {removing === lang ? t('reportGen.translate.removing') : t('reportGen.translate.remove')}
            </SecondaryButton>
          </div>
        );
      })}

      <div style={{ margin: '16px 0 8px', fontSize: 13, fontWeight: 600, color: '#cbd5e1' }}>{t('reportGen.translate.addLanguage')}</div>
      <LanguagePicker
        value={chosen}
        onChange={setChosen}
        exclude={[instanceLanguage, ...stored.map((r) => r.language)].filter(Boolean)}
        disabled={busy}
      />
      <div style={{ fontSize: 12, color: '#9ca3af', margin: '8px 0' }}>{t('reportGen.translate.uiNote')}</div>
      <ActionRow style={{ alignItems: 'center', marginBottom: 12 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#cbd5e1' }}>
          <input type="checkbox" checked={includeTranscripts} onChange={(e) => setIncludeTranscripts(e.target.checked)} disabled={busy} />
          {t('reportGen.translate.includeTranscripts')}
        </label>
        <PrimaryButton type="button" onClick={() => translate(chosen)} disabled={busy || chosen.length === 0}>
          {busy ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Languages size={16} />}
          {t('reportGen.translate.translateCount', { count: chosen.length })}
        </PrimaryButton>
      </ActionRow>
      {job && (
        <StatusMessage>
          {t('reportGen.status.translateRunning', { langs: job.langs.map(name).join(', ') })}
          {typeof job.progress === 'number' ? ` ${job.progress} %` : ''}
        </StatusMessage>
      )}
      {status && <StatusMessage $error={status.error}>{status.text}</StatusMessage>}
    </div>
  );
}
